<script setup lang="ts">
// Vue Imports
import { ref } from "vue"
import { RouterLink, useRoute } from "vue-router"
import {
  Dialog,
  DialogPanel, Menu, MenuButton,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'

// Component Logic
const cdn = import.meta.env.VITE_CDN_MWP_BASE_URL
const route = useRoute()

const logoUrl = `${cdn}/logos/MWP-logo-2.svg`
const loginUrl = `/login`
const crossUrl = `${cdn}/icons/menu-close-cross.svg`
const menuUrl = `${cdn}/Icons/black/3-lines.svg`

const mobileMenuOpen = ref(false)
const openDropdown = ref(null)

const show = (name) => {
  openDropdown.value = name
}
const hide = () => {
  openDropdown.value = null
}
</script>

<template>
  <header class="bg-white border-b border-lighter-grey">
    <nav
      class="flex flex-wrap mx-auto max-w-7xl items-center justify-between gap-8 py-6 px-8 xl:px-0"
      aria-label="Navigation"
    >
      <a
        href="/"
        aria-label="Home logo button"
        :class="{'hover:cursor-auto pointer-events-none' : route.path === '/'}"
      >
        <img :src="logoUrl" alt="My Wealth Private Logo" />
      </a>

      <div class="hidden lg:flex flex-wrap items-center gap-8">
        <PopoverGroup class="flex gap-x-6 xl:gap-x-12 items-center">
          <!-- About us -->
          <div @mouseenter="show('about')" @mouseleave="hide()" class="relative mt-1">
            <Popover class="relative">
              <PopoverButton
                @click="location.href='/about-us'"
                class="flex items-center gap-2 body-3-semi-bold text-granite cursor-pointer"
              >
                <a
                  href="/about-us"
                  class="hover:underline"
                >
                  About Us
                </a>
                <img
                    :src="`${cdn}/Icons/black/arrow-down.svg`"
                    :class="[{ 'rotate-180' : openDropdown === 'about' }, 'flex-none size-5 mt-1']"
                    aria-hidden="true"
                    alt="arrow down icon"
                  />
              </PopoverButton>

              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
              >
                <PopoverPanel
                  v-show="openDropdown === 'about'"
                  static
                  class="absolute -left-9 top-full z-10 mt-4 w-screen max-w-max overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 text-center"
                >
                  <div class="flex flex-col gap-4 p-8 body-3-semi-bold text-granite">
                    <a
                      href="/about-us/our-isa-and-sipp"
                      :class="[
                        route.path === '/about-us/our-isa-and-sipp'
                          ? 'hover:cursor-auto text-sage'
                          : 'hover:underline'
                      ]"
                    >
                      Our ISA &amp; SIPP
                    </a>
                    <a
                      href="/about-us/our-advisers"
                      :class="[
                        route.path === '/about-us/our-advisers'
                          ? 'hover:cursor-auto text-sage'
                          : 'hover:underline'
                      ]"
                    >
                      Our Advisers
                    </a>
                    <a
                      href="/about-us/testimonials"
                      :class="[
                        route.path === '/about-us/testimonials'
                          ? 'hover:cursor-auto text-sage'
                          : 'hover:underline'
                      ]"
                    >
                      Testimonials
                    </a>
                  </div>
                </PopoverPanel>
              </transition>
            </Popover>
          </div>

          <!-- Investment Advice -->
          <a
            :class="[
              route.path === '/investment-advice'
                ? 'hover:cursor-auto text-sage'
                : 'hover:underline',
                'body-3-semi-bold text-granite mt-1'
            ]"
            href="/investment-advice"
          >
            Investment Advice
          </a>

          <!-- Retirement planning -->
          <div @mouseenter="show('retirement')" @mouseleave="hide()" class="relative mt-1">
            <Popover class="relative">
              <PopoverButton
                @click="location.href='/retirement-planning'"
                class="flex items-center gap-2 body-3-semi-bold text-granite cursor-pointer"
              >
                <a
                  href="/retirement-planning"
                  :class="[
                    route.path === '/retirement-planning'
                      ? 'hover:cursor-auto text-sage'
                      : 'hover:underline'
                  ]"
                >
                  Retirement Planning
                </a>
                <img
                  :src="`${cdn}/Icons/black/arrow-down.svg`"
                  :class="[{ 'rotate-180' : openDropdown === 'retirement' }, 'flex-none size-5 mt-1']"
                  aria-hidden="true"
                  alt="arrow down icon"
                />
              </PopoverButton>

              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1"
              >
                <PopoverPanel
                  v-show="openDropdown === 'retirement'"
                  static
                  class="absolute -left-9 top-full z-10 mt-4 w-screen max-w-max overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 text-center"
                >
                  <div class="flex flex-col gap-4 p-8 body-3-semi-bold text-granite">
                    <a
                      href="/retirement-planning/defined-benefit-pensions"
                      :class="[
                        route.path === '/retirement-planning/defined-benefit-pensions'
                          ? 'hover:cursor-auto text-sage'
                          : 'hover:underline'
                      ]"
                    >
                      Defined Benefit Pensions
                    </a>
                  </div>
                </PopoverPanel>
              </transition>
            </Popover>
          </div>

          <a
            href="/news"
            :class="[
              route.path === '/news'
                ? 'hover:cursor-auto text-sage'
                : 'hover:underline',
                'body-3-semi-bold text-granite mt-1'
            ]"
          >
            News
          </a>

          <a
            href="/get-in-touch"
            :class="[
              route.path === '/get-in-touch'
                ? 'hover:cursor-auto text-sage'
                : 'hover:underline',
                'body-3-semi-bold text-granite mt-1'
            ]"
          >
            Get In Touch
          </a>
        </PopoverGroup>

        <div>
          <RouterLink
            :to="loginUrl"
            aria-label="Login button"
            class="primary-mwp button-padding body-3-semi-bold rounded-full max-w-max"
          >
            Log In
          </RouterLink>
        </div>
      </div>

      <div class="flex lg:hidden">
        <Menu as="div" class="relative inline-block text-center">
          <div>
            <MenuButton
              aria-label="Open mobile hamburger menu"
              class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 cursor-pointer"
              @click="mobileMenuOpen = true"
            >
              <span class="sr-only">Open main menu</span>
              <img :src="menuUrl" alt="Open menu icon" />
            </MenuButton>
          </div>
        </Menu>
      </div>
    </nav>

    <TransitionRoot
      :show="mobileMenuOpen"
      as="template"
    >
      <Dialog
        class="block lg:hidden"
        @close="mobileMenuOpen = false"
        :open="mobileMenuOpen"
        aria-label="Mobile Navigation"
      >
        <TransitionChild
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 z-10" />
        </TransitionChild>

        <TransitionChild
          enter="duration-300 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="text-granite fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-10 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
          >
            <div class="flex items-center justify-between">
              <a
                href="/"
                aria-label="Home logo button"
                :class="{'hover:cursor-auto' : $route.path === '/'}"
                @click="mobileMenuOpen = false"
              >
                <img
                  :src="`${cdn}/logos/MWP-logo-2.svg`"
                  alt="My Wealth Private Logo"
                />
              </a>

              <button
                @click="mobileMenuOpen = false"
                aria-label="Close menu button"
              >
                <span class="sr-only">Close main menu</span>
                <img
                  :src="`${cdn}/Icons/black/Close.svg`"
                  aria-hidden="true"
                  alt="x icon to close mobile menu"
                />
              </button>
            </div>

            <div class="flow-root">
              <div class="divide-y divide-gray-500/10">
                <div class="flex flex-col space-y-6 py-6 pl-3 pr-1">
                  <!-- About us -->
                  <div class="flex flex-col">
                    <a
                      href="/about-us"
                      :class="[
                          route.path === '/about-us'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline',
                            'body-1-semi-bold'
                        ]"
                      @click="mobileMenuOpen = false"
                    >
                      About Us
                    </a>
                    <div class="mt-2 ml-3 flex flex-col space-y-2 body-2-reg">
                      <a
                        href="/about-us/our-isa-and-sipp"
                        :class="[
                          route.path === '/about-us/our-isa-and-sipp'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline'
                        ]"
                        @click="mobileMenuOpen = false"
                      >
                        Our ISA &amp; SIPP
                      </a>
                      <a
                        href="/about-us/our-advisers"
                        :class="[
                          route.path === '/about-us/our-advisers'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline'
                        ]"
                        @click="mobileMenuOpen = false"
                      >
                        Our Advisers
                      </a>
                      <a
                        href="/about-us/testimonials"
                        :class="[
                          route.path === '/about-us/testimonials'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline'
                        ]"
                        @click="mobileMenuOpen = false"
                      >
                        Testimonials
                      </a>
                    </div>
                  </div>

                  <!-- Investment advice -->
                  <a
                    :class="[
                          route.path === '/investment-advice'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline',
                            'body-1-semi-bold'
                        ]"
                    href="/investment-advice"
                    @click="mobileMenuOpen = false"
                  >
                    Investment Advice
                  </a>

                  <!-- Retirement planning -->
                  <div class="flex flex-col">
                    <a
                      href="/retirement-planning"
                      :class="[
                          route.path === '/retirement-planning'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline',
                            'body-1-semi-bold'
                        ]"
                      @click="mobileMenuOpen = false"
                    >
                      Retirement Planning
                    </a>
                    <div class="mt-2 ml-3 flex flex-col space-y-2 body-2-reg">
                      <a
                        href="/retirement-planning/defined-benefit-pensions"
                        :class="[
                          route.path === '/retirement-planning/defined-benefit-pensions'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline'
                        ]"
                        @click="mobileMenuOpen = false"
                      >
                        Defined Benefit Pensions
                      </a>
                    </div>
                  </div>

                  <a
                    href="/news"
                    :class="[
                          route.path === '/news'
                            ? 'hover:cursor-auto text-sage'
                            : 'hover:underline',
                            'body-1-semi-bold'
                        ]"
                    @click="mobileMenuOpen = false"
                  >
                    News
                  </a>

                  <a
                    href="/get-in-touch"
                    :class="[
                      route.path === '/get-in-touch'
                        ? 'hover:cursor-auto text-sage'
                        : 'hover:underline',
                        'body-1-semi-bold'
                    ]"
                    @click="mobileMenuOpen = false"
                  >
                    Get In Touch
                  </a>
                </div>

                <div class="py-6">
                  <a
                    :href="loginUrl"
                    target="_blank"
                    aria-label="Login button"
                    class="primary-mwp button-padding body-3-semi-bold rounded-full mx-auto lg:mx-0 w-max"
                    @click="mobileMenuOpen = false"
                  >
                    Log In
                  </a>
                </div>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </TransitionRoot>
  </header>
</template>
